import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Send,
  RefreshCw,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Pencil,
  LogOut,
  AlertCircle,
  Inbox,
  Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const GMAIL_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GMAIL_SCOPES = 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send';

const gmailFetch = async (url, token, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`;
    try {
      const err = await res.json();
      errMsg = err.error?.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }
  return res.json();
};

const decodeBase64Url = (str) => {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  try {
    return decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    return atob(base64);
  }
};

const extractTextFromPayload = (payload) => {
  if (!payload) return '';
  if (payload.body?.data) return decodeBase64Url(payload.body.data);
  if (payload.parts) {
    const plain = payload.parts.find((p) => p.mimeType === 'text/plain');
    if (plain?.body?.data) return decodeBase64Url(plain.body.data);
    for (const part of payload.parts) {
      const text = extractTextFromPayload(part);
      if (text) return text;
    }
  }
  return '';
};

const getHeader = (headers = [], name) =>
  headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || '';

const buildQuery = (supervisorEmails, subjectFilters) => {
  const parts = [];
  if (supervisorEmails.length > 0) {
    const ep = supervisorEmails.map((e) => `(from:${e} OR to:${e})`).join(' OR ');
    parts.push(`(${ep})`);
  }
  if (subjectFilters.length > 0) {
    const sp = subjectFilters.map((s) => `subject:"${s}"`).join(' OR ');
    parts.push(`(${sp})`);
  }
  return parts.join(' OR ');
};

const formatMsgDate = (internalDate) => {
  try {
    const d = new Date(parseInt(internalDate, 10));
    return format(d, 'MMM d, yyyy');
  } catch {
    return '';
  }
};

const GmailPanel = ({
  supervisorEmails: initSupervisorEmails = [],
  emailSubjectFilters: initSubjectFilters = [],
  onUpdateFilters,
  contextTitle = '',
}) => {
  const [scriptsReady, setScriptsReady] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Filters (local state — saved via onUpdateFilters)
  const [supervisorEmails, setSupervisorEmails] = useState(initSupervisorEmails);
  const [subjectFilters, setSubjectFilters] = useState(initSubjectFilters);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [newSubjectInput, setNewSubjectInput] = useState('');

  // Threads
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [fullThreads, setFullThreads] = useState({});
  const [fullThreadLoading, setFullThreadLoading] = useState({});

  // Compose
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);

  // ── Bootstrap Google scripts ──────────────────────────────────────────────
  useEffect(() => {
    const init = () => {
      if (!window.google?.accounts?.oauth2 || !GMAIL_CLIENT_ID) return;
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GMAIL_CLIENT_ID,
        scope: GMAIL_SCOPES,
        callback: async (response) => {
          if (response.error) {
            setAuthError(response.error_description || response.error);
            return;
          }
          if (response.access_token) {
            setAccessToken(response.access_token);
            setAuthError(null);
            try {
              const profile = await gmailFetch(
                'https://gmail.googleapis.com/gmail/v1/users/me/profile',
                response.access_token
              );
              setUserEmail(profile.emailAddress);
            } catch {}
          }
        },
      });
      setTokenClient(client);
      setScriptsReady(true);
    };

    if (window.google?.accounts?.oauth2) {
      init();
    } else {
      // GSI script may still be loading (GoogleDocsPicker loads it too)
      const existing = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      if (existing) {
        existing.addEventListener('load', init);
      } else {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = init;
        document.body.appendChild(script);
      }
    }
  }, []);

  // ── Fetch threads ─────────────────────────────────────────────────────────
  const fetchThreads = useCallback(async () => {
    if (!accessToken) return;
    const query = buildQuery(supervisorEmails, subjectFilters);
    if (!query) return;

    setThreadsLoading(true);
    setThreads([]);
    setExpandedId(null);
    try {
      const listData = await gmailFetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/threads?q=${encodeURIComponent(query)}&maxResults=20`,
        accessToken
      );

      if (!listData.threads?.length) {
        setThreads([]);
        return;
      }

      const metaResults = await Promise.all(
        listData.threads.map((t) =>
          gmailFetch(
            `https://gmail.googleapis.com/gmail/v1/users/me/threads/${t.id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Date`,
            accessToken
          ).catch(() => ({ id: t.id, messages: [], snippet: t.snippet }))
        )
      );
      setThreads(metaResults);
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('invalid_token')) {
        setAccessToken(null);
        setAuthError('Session expired. Please reconnect Gmail.');
      } else {
        toast.error('Failed to fetch emails: ' + err.message);
      }
    } finally {
      setThreadsLoading(false);
    }
  }, [accessToken, supervisorEmails, subjectFilters]);

  useEffect(() => {
    if (accessToken && (supervisorEmails.length > 0 || subjectFilters.length > 0)) {
      fetchThreads();
    }
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Expand thread (load full messages) ────────────────────────────────────
  const handleExpandThread = async (threadId) => {
    if (expandedId === threadId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(threadId);
    if (fullThreads[threadId]) return;

    setFullThreadLoading((prev) => ({ ...prev, [threadId]: true }));
    try {
      const data = await gmailFetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}?format=full`,
        accessToken
      );
      setFullThreads((prev) => ({ ...prev, [threadId]: data }));
    } catch {
      toast.error('Failed to load thread');
    } finally {
      setFullThreadLoading((prev) => ({ ...prev, [threadId]: false }));
    }
  };

  // ── Send email ────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim()) {
      toast.error('Fill in all fields before sending');
      return;
    }
    setComposeSending(true);
    try {
      const mime = [
        `To: ${composeTo}`,
        `Subject: ${composeSubject}`,
        `Content-Type: text/plain; charset=utf-8`,
        ``,
        composeBody,
      ].join('\r\n');

      const encoded = btoa(unescape(encodeURIComponent(mime)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      await gmailFetch(
        'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
        accessToken,
        { method: 'POST', body: JSON.stringify({ raw: encoded }) }
      );

      toast.success('Email sent!');
      setComposeOpen(false);
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      fetchThreads();
    } catch (err) {
      toast.error('Failed to send: ' + err.message);
    } finally {
      setComposeSending(false);
    }
  };

  // ── Filter helpers ────────────────────────────────────────────────────────
  const saveFilters = (emails, subjects) => {
    onUpdateFilters({ supervisorEmails: emails, emailSubjectFilters: subjects });
  };

  const addEmail = () => {
    const val = newEmailInput.trim().toLowerCase();
    if (!val || !val.includes('@')) { toast.error('Enter a valid email address'); return; }
    if (supervisorEmails.includes(val)) { setNewEmailInput(''); return; }
    const updated = [...supervisorEmails, val];
    setSupervisorEmails(updated);
    setNewEmailInput('');
    saveFilters(updated, subjectFilters);
  };

  const removeEmail = (e) => {
    const updated = supervisorEmails.filter((x) => x !== e);
    setSupervisorEmails(updated);
    saveFilters(updated, subjectFilters);
  };

  const addSubject = () => {
    const val = newSubjectInput.trim();
    if (!val) return;
    if (subjectFilters.includes(val)) { setNewSubjectInput(''); return; }
    const updated = [...subjectFilters, val];
    setSubjectFilters(updated);
    setNewSubjectInput('');
    saveFilters(supervisorEmails, updated);
  };

  const removeSubject = (s) => {
    const updated = subjectFilters.filter((x) => x !== s);
    setSubjectFilters(updated);
    saveFilters(supervisorEmails, updated);
  };

  const openCompose = () => {
    setComposeTo(supervisorEmails.join(', '));
    setComposeSubject(contextTitle ? `Re: ${contextTitle}` : '');
    setComposeBody('');
    setComposeOpen(true);
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const FilterSection = () => (
    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-200 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">Email Filters</span>
        <span className="text-xs text-slate-400 ml-1">(saved automatically)</span>
      </div>

      {/* Supervisor emails */}
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Supervisor / Contact Emails</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {supervisorEmails.map((e) => (
            <Badge key={e} variant="secondary" className="gap-1 pr-1">
              {e}
              <button
                type="button"
                onClick={() => removeEmail(e)}
                className="rounded-full hover:bg-slate-300 p-0.5"
                aria-label={`Remove ${e}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newEmailInput}
            onChange={(e) => setNewEmailInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addEmail()}
            placeholder="supervisor@school.com"
            className="h-8 text-sm flex-1"
          />
          <Button size="sm" variant="outline" onClick={addEmail} className="h-8 px-2">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Subject filters */}
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Subject Keywords</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {subjectFilters.map((s) => (
            <Badge key={s} variant="secondary" className="gap-1 pr-1">
              {s}
              <button
                type="button"
                onClick={() => removeSubject(s)}
                className="rounded-full hover:bg-slate-300 p-0.5"
                aria-label={`Remove ${s}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newSubjectInput}
            onChange={(e) => setNewSubjectInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSubject()}
            placeholder="e.g. EE Draft, IB Extended Essay"
            className="h-8 text-sm flex-1"
          />
          <Button size="sm" variant="outline" onClick={addSubject} className="h-8 px-2">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );

  // ── Not connected ─────────────────────────────────────────────────────────
  if (!accessToken) {
    return (
      <div className="space-y-4">
        <FilterSection />

        {authError && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 text-sm">{authError}</AlertDescription>
          </Alert>
        )}

        {!GMAIL_CLIENT_ID ? (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              Google Client ID not configured. Set <code>REACT_APP_GOOGLE_CLIENT_ID</code> to enable Gmail.
            </AlertDescription>
          </Alert>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center space-y-3">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <p className="font-medium text-slate-800 mb-1">Connect Gmail</p>
                <p className="text-sm text-slate-500">
                  View matching email threads and compose messages to your supervisor
                </p>
              </div>
              <Button
                onClick={() => tokenClient?.requestAccessToken({ prompt: '' })}
                disabled={!scriptsReady || !tokenClient}
              >
                {!scriptsReady ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Connect Gmail
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ── Connected ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Mail className="w-4 h-4 text-green-600" />
          <span className="font-medium">{userEmail || 'Gmail connected'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={fetchThreads} disabled={threadsLoading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${threadsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={openCompose} disabled={supervisorEmails.length === 0}>
            <Pencil className="w-3.5 h-3.5 mr-1.5" />
            Compose
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setAccessToken(null); setUserEmail(null); setThreads([]); }}
            title="Disconnect Gmail"
          >
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <FilterSection />

      {/* No filters set */}
      {supervisorEmails.length === 0 && subjectFilters.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-4">
          Add at least one filter above, then click Refresh to load emails.
        </p>
      )}

      {/* Thread list */}
      {threadsLoading && (
        <div className="text-center py-8 text-sm text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-400" />
          Loading emails…
        </div>
      )}

      {!threadsLoading && threads.length === 0 && (supervisorEmails.length > 0 || subjectFilters.length > 0) && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium text-slate-700 mb-1">No matching threads found</p>
            <p className="text-xs text-slate-500">Try adjusting your email or subject filters</p>
          </CardContent>
        </Card>
      )}

      {threads.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 font-medium">{threads.length} thread{threads.length !== 1 ? 's' : ''} found</p>
          {threads.map((thread) => {
            const messages = thread.messages || [];
            const firstMsg = messages[0];
            const lastMsg = messages[messages.length - 1];
            const subject = getHeader(firstMsg?.payload?.headers, 'subject') || '(no subject)';
            const from = getHeader(lastMsg?.payload?.headers, 'from') || '';
            const dateStr = lastMsg?.internalDate ? formatMsgDate(lastMsg.internalDate) : '';
            const isExpanded = expandedId === thread.id;
            const fullData = fullThreads[thread.id];
            const fullLoading = fullThreadLoading[thread.id];

            return (
              <Card key={thread.id} className="overflow-hidden">
                <button
                  type="button"
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                  onClick={() => handleExpandThread(thread.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-slate-900 text-sm truncate">{subject}</span>
                        {messages.length > 1 && (
                          <Badge variant="secondary" className="text-[10px] h-4 px-1 shrink-0">
                            {messages.length}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {from.replace(/<[^>]+>/, '').trim()} · {dateStr}
                      </p>
                    </div>
                    <div className="shrink-0 text-slate-400 mt-0.5">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50">
                    {fullLoading && (
                      <div className="p-4 text-center text-sm text-slate-500">
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1" />
                        Loading…
                      </div>
                    )}
                    {!fullLoading && fullData && (
                      <div className="divide-y divide-slate-100">
                        {fullData.messages.map((msg) => {
                          const msgFrom = getHeader(msg.payload?.headers, 'from');
                          const msgDate = msg.internalDate ? formatMsgDate(msg.internalDate) : '';
                          const body = extractTextFromPayload(msg.payload);

                          return (
                            <div key={msg.id} className="px-4 py-3">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-slate-700 truncate">{msgFrom}</span>
                                <span className="text-xs text-slate-400 shrink-0 ml-2">{msgDate}</span>
                              </div>
                              <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">
                                {body.trim() || <span className="italic text-slate-400">(no text content)</span>}
                              </pre>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              New Email
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">To</label>
              <Input
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                placeholder="supervisor@school.com"
                className="text-sm"
              />
              {supervisorEmails.length > 1 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {supervisorEmails.map((e) => (
                    <button
                      key={e}
                      type="button"
                      className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full hover:bg-indigo-100"
                      onClick={() => setComposeTo(e)}
                    >
                      {e}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full hover:bg-slate-200"
                    onClick={() => setComposeTo(supervisorEmails.join(', '))}
                  >
                    All
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">Subject</label>
              <Input
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Subject"
                className="text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">Message</label>
              <Textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                placeholder="Write your message…"
                rows={8}
                className="text-sm resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setComposeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={composeSending}>
              {composeSending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {composeSending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GmailPanel;
